/**
 * EV / PHEV model database for the LaadCheck calculator.
 * Covers all 43 brands in MERKEN with their main electric and plug-in hybrid models.
 * Specs: WLTP usable battery (kWh), charge-depleting consumption (kWh/100 km),
 * max onboard AC charger (kW), number of AC phases.
 * PHEV kwhPer100Km = charge-depleting EV-mode consumption.
 */

export interface AutoModel {
  merk: string;
  model: string;
  /** Battery capacity in kWh */
  accuKwh: number;
  /** Consumption in kWh/100 km */
  kwhPer100Km: number;
  /** Max AC charging power in kW */
  maxAcKw: number;
  /** Number of phases */
  fase: 1 | 3;
}

export const AUTO_MODELLEN: AutoModel[] = [
  // ── Abarth ──────────────────────────────────────────────────────────────────
  { merk: 'Abarth',        model: '500e',                  accuKwh: 42,  kwhPer100Km: 14.5, maxAcKw: 11,  fase: 3 },

  // ── Alfa Romeo ───────────────────────────────────────────────────────────────
  { merk: 'Alfa Romeo',    model: 'Junior Elettrica',         accuKwh: 54,  kwhPer100Km: 15.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Alfa Romeo',    model: 'Junior Elettrica Veloce',  accuKwh: 54,  kwhPer100Km: 16.5, maxAcKw: 11,  fase: 3 },

  // ── Alpine ───────────────────────────────────────────────────────────────────
  { merk: 'Alpine',        model: 'A290',                  accuKwh: 52,  kwhPer100Km: 14.0, maxAcKw: 11,  fase: 3 },

  // ── Audi ─────────────────────────────────────────────────────────────────────
  { merk: 'Audi',          model: 'Q4 e-tron',             accuKwh: 77,  kwhPer100Km: 18.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Audi',          model: 'Q4 Sportback e-tron',   accuKwh: 77,  kwhPer100Km: 18.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Audi',          model: 'Q6 e-tron',             accuKwh: 100, kwhPer100Km: 19.0, maxAcKw: 22,  fase: 3 },
  { merk: 'Audi',          model: 'A6 e-tron',             accuKwh: 100, kwhPer100Km: 17.5, maxAcKw: 22,  fase: 3 },
  { merk: 'Audi',          model: 'Q8 e-tron',              accuKwh: 86,  kwhPer100Km: 24.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Audi',          model: 'Q8 Sportback e-tron',   accuKwh: 86,  kwhPer100Km: 23.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Audi',          model: 'e-tron GT',             accuKwh: 93,  kwhPer100Km: 20.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Audi',          model: 'A3 TFSI e (PHEV)',      accuKwh: 13,  kwhPer100Km: 17.0, maxAcKw: 3.7, fase: 1 },
  { merk: 'Audi',          model: 'Q5 TFSI e (PHEV)',      accuKwh: 14,  kwhPer100Km: 23.0, maxAcKw: 7.4, fase: 1 },

  // ── BMW ──────────────────────────────────────────────────────────────────────
  { merk: 'BMW',           model: 'iX1',                   accuKwh: 64,  kwhPer100Km: 17.0, maxAcKw: 11,  fase: 3 },
  { merk: 'BMW',           model: 'i4',                    accuKwh: 80,  kwhPer100Km: 16.5, maxAcKw: 11,  fase: 3 },
  { merk: 'BMW',           model: 'iX3',                   accuKwh: 80,  kwhPer100Km: 18.5, maxAcKw: 11,  fase: 3 },
  { merk: 'BMW',           model: 'iX',                    accuKwh: 111, kwhPer100Km: 21.0, maxAcKw: 11,  fase: 3 },
  { merk: 'BMW',           model: 'i5',                    accuKwh: 84,  kwhPer100Km: 18.0, maxAcKw: 22,  fase: 3 },
  { merk: 'BMW',           model: 'i7',                    accuKwh: 105, kwhPer100Km: 21.0, maxAcKw: 22,  fase: 3 },
  { merk: 'BMW',           model: 'X1 xDrive30e (PHEV)',   accuKwh: 14,  kwhPer100Km: 16.0, maxAcKw: 3.7, fase: 1 },
  { merk: 'BMW',           model: '330e (PHEV)',            accuKwh: 12,  kwhPer100Km: 22.0, maxAcKw: 3.7, fase: 1 },
  { merk: 'BMW',           model: '530e (PHEV)',            accuKwh: 19,  kwhPer100Km: 21.0, maxAcKw: 11,  fase: 3 },
  { merk: 'BMW',           model: 'X5 xDrive50e (PHEV)',   accuKwh: 25,  kwhPer100Km: 25.0, maxAcKw: 11,  fase: 3 },

  // ── BYD ──────────────────────────────────────────────────────────────────────
  { merk: 'BYD',           model: 'Atto 3',                accuKwh: 60,  kwhPer100Km: 17.0, maxAcKw: 7.4, fase: 1 },
  { merk: 'BYD',           model: 'Seal',                  accuKwh: 82,  kwhPer100Km: 16.5, maxAcKw: 11,  fase: 3 },
  { merk: 'BYD',           model: 'Dolphin',               accuKwh: 60,  kwhPer100Km: 14.5, maxAcKw: 11,  fase: 3 },
  { merk: 'BYD',           model: 'Seal U',                accuKwh: 87,  kwhPer100Km: 18.0, maxAcKw: 11,  fase: 3 },
  { merk: 'BYD',           model: 'Atto 2',                accuKwh: 46,  kwhPer100Km: 14.0, maxAcKw: 7.4, fase: 1 },

  // ── Citroën ──────────────────────────────────────────────────────────────────
  { merk: 'Citroën',       model: 'ë-C3',                  accuKwh: 44,  kwhPer100Km: 14.0, maxAcKw: 7.4, fase: 1 },
  { merk: 'Citroën',       model: 'ë-C4',                  accuKwh: 54,  kwhPer100Km: 16.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Citroën',       model: 'ë-C4 X',                accuKwh: 54,  kwhPer100Km: 15.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Citroën',       model: 'ë-Berlingo',            accuKwh: 50,  kwhPer100Km: 17.0, maxAcKw: 7.4, fase: 1 },

  // ── Cupra ────────────────────────────────────────────────────────────────────
  { merk: 'Cupra',         model: 'Born',                  accuKwh: 58,  kwhPer100Km: 15.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Cupra',         model: 'Tavascan',              accuKwh: 77,  kwhPer100Km: 18.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Cupra',         model: 'Raval',                 accuKwh: 38,  kwhPer100Km: 14.0, maxAcKw: 7.4, fase: 1 },

  // ── Dacia ────────────────────────────────────────────────────────────────────
  { merk: 'Dacia',         model: 'Spring',                accuKwh: 27,  kwhPer100Km: 13.9, maxAcKw: 7.4, fase: 1 },

  // ── DS Automobiles ───────────────────────────────────────────────────────────
  { merk: 'DS Automobiles',model: 'DS 3 E-Tense',          accuKwh: 54,  kwhPer100Km: 15.5, maxAcKw: 11,  fase: 3 },
  { merk: 'DS Automobiles',model: 'DS 4 E-Tense',          accuKwh: 54,  kwhPer100Km: 16.5, maxAcKw: 11,  fase: 3 },
  { merk: 'DS Automobiles',model: 'DS 7 E-Tense (PHEV)',   accuKwh: 15,  kwhPer100Km: 20.0, maxAcKw: 7.4, fase: 1 },

  // ── Fiat ─────────────────────────────────────────────────────────────────────
  { merk: 'Fiat',          model: '500e',                  accuKwh: 42,  kwhPer100Km: 14.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Fiat',          model: '600e',                  accuKwh: 54,  kwhPer100Km: 15.0, maxAcKw: 11,  fase: 3 },

  // ── Ford ─────────────────────────────────────────────────────────────────────
  { merk: 'Ford',          model: 'Explorer EV',           accuKwh: 82,  kwhPer100Km: 17.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Ford',          model: 'Mustang Mach-E',        accuKwh: 91,  kwhPer100Km: 20.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Ford',          model: 'Puma Gen-E',            accuKwh: 43,  kwhPer100Km: 15.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Ford',          model: 'Kuga PHEV',             accuKwh: 14,  kwhPer100Km: 21.0, maxAcKw: 3.7, fase: 1 },

  // ── Genesis ──────────────────────────────────────────────────────────────────
  { merk: 'Genesis',       model: 'GV60',                  accuKwh: 77,  kwhPer100Km: 18.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Genesis',       model: 'GV70 Electrified',      accuKwh: 77,  kwhPer100Km: 19.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Genesis',       model: 'G80 Electrified',       accuKwh: 87,  kwhPer100Km: 19.5, maxAcKw: 11,  fase: 3 },

  // ── Honda ────────────────────────────────────────────────────────────────────
  { merk: 'Honda',         model: 'e',                     accuKwh: 35,  kwhPer100Km: 18.0, maxAcKw: 7.4, fase: 1 },
  { merk: 'Honda',         model: 'e:Ny1',                 accuKwh: 68,  kwhPer100Km: 17.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Honda',         model: 'CR-V e:PHEV',           accuKwh: 17,  kwhPer100Km: 21.0, maxAcKw: 7.4, fase: 1 },

  // ── Hyundai ──────────────────────────────────────────────────────────────────
  { merk: 'Hyundai',       model: 'Kona Electric',         accuKwh: 64,  kwhPer100Km: 15.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Hyundai',       model: 'IONIQ 5',               accuKwh: 77,  kwhPer100Km: 18.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Hyundai',       model: 'IONIQ 5 N',             accuKwh: 84,  kwhPer100Km: 20.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Hyundai',       model: 'IONIQ 6',               accuKwh: 77,  kwhPer100Km: 14.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Hyundai',       model: 'IONIQ 9',               accuKwh: 110, kwhPer100Km: 21.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Hyundai',       model: 'Tucson Plug-in Hybrid', accuKwh: 14,  kwhPer100Km: 23.0, maxAcKw: 7.4, fase: 1 },

  // ── Jaguar ───────────────────────────────────────────────────────────────────
  { merk: 'Jaguar',        model: 'I-PACE',                accuKwh: 90,  kwhPer100Km: 25.0, maxAcKw: 11,  fase: 3 },

  // ── Jeep ─────────────────────────────────────────────────────────────────────
  { merk: 'Jeep',          model: 'Avenger EV',            accuKwh: 54,  kwhPer100Km: 15.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Jeep',          model: 'Wrangler 4xe (PHEV)',   accuKwh: 17,  kwhPer100Km: 40.0, maxAcKw: 7.2, fase: 1 },
  { merk: 'Jeep',          model: 'Compass 4xe (PHEV)',    accuKwh: 11,  kwhPer100Km: 20.0, maxAcKw: 3.7, fase: 1 },

  // ── Kia ──────────────────────────────────────────────────────────────────────
  { merk: 'Kia',           model: 'EV3',                   accuKwh: 58,  kwhPer100Km: 14.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Kia',           model: 'EV6',                   accuKwh: 77,  kwhPer100Km: 18.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Kia',           model: 'EV9',                   accuKwh: 99,  kwhPer100Km: 22.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Kia',           model: 'Niro EV',               accuKwh: 64,  kwhPer100Km: 16.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Kia',           model: 'Niro Plug-in Hybrid',   accuKwh: 9,   kwhPer100Km: 18.0, maxAcKw: 3.7, fase: 1 },
  { merk: 'Kia',           model: 'Sportage Plug-in Hybrid',accuKwh: 14, kwhPer100Km: 20.0, maxAcKw: 7.4, fase: 1 },
  { merk: 'Kia',           model: 'Sorento Plug-in Hybrid', accuKwh: 14, kwhPer100Km: 27.0, maxAcKw: 7.4, fase: 1 },

  // ── Leapmotor ────────────────────────────────────────────────────────────────
  { merk: 'Leapmotor',     model: 'T03',                   accuKwh: 37,  kwhPer100Km: 15.0, maxAcKw: 7.4, fase: 1 },
  { merk: 'Leapmotor',     model: 'C10',                   accuKwh: 69,  kwhPer100Km: 16.5, maxAcKw: 11,  fase: 3 },

  // ── Lexus ────────────────────────────────────────────────────────────────────
  { merk: 'Lexus',         model: 'UX 300e',               accuKwh: 72,  kwhPer100Km: 21.0, maxAcKw: 7.4, fase: 1 },
  { merk: 'Lexus',         model: 'RZ 450e',               accuKwh: 71,  kwhPer100Km: 20.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Lexus',         model: 'NX 450h+ (PHEV)',       accuKwh: 18,  kwhPer100Km: 24.0, maxAcKw: 7.4, fase: 1 },
  { merk: 'Lexus',         model: 'RX 450h+ (PHEV)',       accuKwh: 18,  kwhPer100Km: 27.0, maxAcKw: 7.4, fase: 1 },

  // ── Lotus ────────────────────────────────────────────────────────────────────
  { merk: 'Lotus',         model: 'Eletre',                accuKwh: 112, kwhPer100Km: 25.0, maxAcKw: 22,  fase: 3 },
  { merk: 'Lotus',         model: 'Emeya',                 accuKwh: 102, kwhPer100Km: 22.0, maxAcKw: 22,  fase: 3 },

  // ── Lynk & Co ────────────────────────────────────────────────────────────────
  { merk: 'Lynk & Co',     model: '01 PHEV',               accuKwh: 14,  kwhPer100Km: 18.0, maxAcKw: 3.7, fase: 1 },
  { merk: 'Lynk & Co',     model: 'Zero EV',               accuKwh: 63,  kwhPer100Km: 17.0, maxAcKw: 11,  fase: 3 },

  // ── Maserati ─────────────────────────────────────────────────────────────────
  { merk: 'Maserati',      model: 'GranTurismo Folgore',   accuKwh: 92,  kwhPer100Km: 22.0, maxAcKw: 22,  fase: 3 },
  { merk: 'Maserati',      model: 'GranCabrio Folgore',    accuKwh: 92,  kwhPer100Km: 23.0, maxAcKw: 22,  fase: 3 },

  // ── Mazda ────────────────────────────────────────────────────────────────────
  { merk: 'Mazda',         model: 'MX-30',                 accuKwh: 35,  kwhPer100Km: 16.5, maxAcKw: 7.4, fase: 1 },
  { merk: 'Mazda',         model: 'MX-30 R-EV (PHEV)',     accuKwh: 17,  kwhPer100Km: 19.0, maxAcKw: 7.4, fase: 1 },
  { merk: 'Mazda',         model: 'CX-80 PHEV',            accuKwh: 17,  kwhPer100Km: 18.5, maxAcKw: 3.7, fase: 1 },

  // ── Mercedes ─────────────────────────────────────────────────────────────────
  // BEV
  { merk: 'Mercedes',      model: 'EQA',                              accuKwh: 66,  kwhPer100Km: 17.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Mercedes',      model: 'EQB',                              accuKwh: 66,  kwhPer100Km: 18.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Mercedes',      model: 'EQC',                              accuKwh: 80,  kwhPer100Km: 22.0, maxAcKw: 7.4, fase: 1 },
  { merk: 'Mercedes',      model: 'EQE',                              accuKwh: 90,  kwhPer100Km: 19.5, maxAcKw: 22,  fase: 3 },
  { merk: 'Mercedes',      model: 'EQE SUV',                          accuKwh: 90,  kwhPer100Km: 21.5, maxAcKw: 22,  fase: 3 },
  { merk: 'Mercedes',      model: 'EQS',                              accuKwh: 107, kwhPer100Km: 20.5, maxAcKw: 22,  fase: 3 },
  { merk: 'Mercedes',      model: 'EQS SUV',                          accuKwh: 107, kwhPer100Km: 24.0, maxAcKw: 22,  fase: 3 },
  // PHEV — compact
  { merk: 'Mercedes',      model: 'A 250 e (PHEV)',                   accuKwh: 16,  kwhPer100Km: 14.5, maxAcKw: 7.4, fase: 1 },
  { merk: 'Mercedes',      model: 'CLA 250 e Coupé (PHEV)',           accuKwh: 16,  kwhPer100Km: 14.5, maxAcKw: 7.4, fase: 1 },
  { merk: 'Mercedes',      model: 'CLA 250 e Shooting Brake (PHEV)',  accuKwh: 16,  kwhPer100Km: 14.5, maxAcKw: 7.4, fase: 1 },
  // PHEV — sedan & estate
  { merk: 'Mercedes',      model: 'C 300e (PHEV)',                    accuKwh: 17,  kwhPer100Km: 17.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Mercedes',      model: 'CLE 300 e Coupé (PHEV)',           accuKwh: 22,  kwhPer100Km: 18.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Mercedes',      model: 'E 300 e (PHEV)',                   accuKwh: 25,  kwhPer100Km: 18.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Mercedes',      model: 'S 580 e (PHEV)',                   accuKwh: 28,  kwhPer100Km: 17.5, maxAcKw: 11,  fase: 3 },
  // PHEV — SUV
  { merk: 'Mercedes',      model: 'GLA 250 e (PHEV)',                 accuKwh: 16,  kwhPer100Km: 16.5, maxAcKw: 7.4, fase: 1 },
  { merk: 'Mercedes',      model: 'GLC 300e (PHEV)',                  accuKwh: 23,  kwhPer100Km: 19.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Mercedes',      model: 'GLC 350 e (PHEV)',                 accuKwh: 9,   kwhPer100Km: 21.0, maxAcKw: 3.7, fase: 1 },
  { merk: 'Mercedes',      model: 'GLE 350 e (PHEV)',                 accuKwh: 28,  kwhPer100Km: 22.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Mercedes',      model: 'GLE 400 e (PHEV)',                 accuKwh: 28,  kwhPer100Km: 23.0, maxAcKw: 11,  fase: 3 },
  // PHEV — AMG E PERFORMANCE
  { merk: 'Mercedes',      model: 'AMG C 63 S E PERFORMANCE (PHEV)',  accuKwh: 6,   kwhPer100Km: 17.0, maxAcKw: 3.7, fase: 1 },
  { merk: 'Mercedes',      model: 'AMG GT 63 S E PERFORMANCE (PHEV)', accuKwh: 6,   kwhPer100Km: 18.0, maxAcKw: 3.7, fase: 1 },
  { merk: 'Mercedes',      model: 'AMG S 63 E PERFORMANCE (PHEV)',    accuKwh: 13,  kwhPer100Km: 18.0, maxAcKw: 7.4, fase: 1 },

  // ── MG ───────────────────────────────────────────────────────────────────────
  { merk: 'MG',            model: 'MG4',                   accuKwh: 64,  kwhPer100Km: 16.0, maxAcKw: 11,  fase: 3 },
  { merk: 'MG',            model: 'MG4 XPOWER',            accuKwh: 64,  kwhPer100Km: 17.5, maxAcKw: 11,  fase: 3 },
  { merk: 'MG',            model: 'ZS EV',                 accuKwh: 72,  kwhPer100Km: 17.5, maxAcKw: 11,  fase: 3 },
  { merk: 'MG',            model: 'Marvel R',              accuKwh: 70,  kwhPer100Km: 18.5, maxAcKw: 11,  fase: 3 },
  { merk: 'MG',            model: 'HS PHEV',               accuKwh: 16,  kwhPer100Km: 18.5, maxAcKw: 3.7, fase: 1 },

  // ── Mini ─────────────────────────────────────────────────────────────────────
  { merk: 'Mini',          model: 'Cooper SE',             accuKwh: 54,  kwhPer100Km: 15.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Mini',          model: 'Aceman E',              accuKwh: 54,  kwhPer100Km: 15.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Mini',          model: 'Countryman E',          accuKwh: 64,  kwhPer100Km: 18.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Mini',          model: 'Countryman SE ALL4 (PHEV)', accuKwh: 15, kwhPer100Km: 21.0, maxAcKw: 7.4, fase: 1 },

  // ── Nissan ───────────────────────────────────────────────────────────────────
  { merk: 'Nissan',        model: 'Leaf 40 kWh',           accuKwh: 40,  kwhPer100Km: 17.5, maxAcKw: 7.4, fase: 1 },
  { merk: 'Nissan',        model: 'Leaf 62 kWh',           accuKwh: 62,  kwhPer100Km: 18.5, maxAcKw: 7.4, fase: 1 },
  { merk: 'Nissan',        model: 'Ariya',                 accuKwh: 87,  kwhPer100Km: 20.0, maxAcKw: 22,  fase: 3 },

  // ── Opel ─────────────────────────────────────────────────────────────────────
  { merk: 'Opel',          model: 'Corsa Electric',        accuKwh: 51,  kwhPer100Km: 15.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Opel',          model: 'Astra Electric',        accuKwh: 54,  kwhPer100Km: 16.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Opel',          model: 'Mokka Electric',        accuKwh: 54,  kwhPer100Km: 15.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Opel',          model: 'Grandland Electric',    accuKwh: 73,  kwhPer100Km: 17.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Opel',          model: 'Frontera Electric',     accuKwh: 44,  kwhPer100Km: 14.5, maxAcKw: 7.4, fase: 1 },

  // ── Peugeot ──────────────────────────────────────────────────────────────────
  { merk: 'Peugeot',       model: 'e-208',                 accuKwh: 50,  kwhPer100Km: 15.5, maxAcKw: 7.4, fase: 1 },
  { merk: 'Peugeot',       model: 'e-2008',                accuKwh: 50,  kwhPer100Km: 17.0, maxAcKw: 7.4, fase: 1 },
  { merk: 'Peugeot',       model: 'e-308',                 accuKwh: 54,  kwhPer100Km: 16.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Peugeot',       model: 'e-3008',                accuKwh: 73,  kwhPer100Km: 16.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Peugeot',       model: 'e-5008',                accuKwh: 73,  kwhPer100Km: 17.0, maxAcKw: 11,  fase: 3 },

  // ── Polestar ─────────────────────────────────────────────────────────────────
  { merk: 'Polestar',      model: '2',                     accuKwh: 78,  kwhPer100Km: 18.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Polestar',      model: '3',                     accuKwh: 111, kwhPer100Km: 21.0, maxAcKw: 22,  fase: 3 },
  { merk: 'Polestar',      model: '4',                     accuKwh: 100, kwhPer100Km: 19.5, maxAcKw: 22,  fase: 3 },

  // ── Porsche ──────────────────────────────────────────────────────────────────
  { merk: 'Porsche',       model: 'Taycan',                accuKwh: 93,  kwhPer100Km: 21.5, maxAcKw: 22,  fase: 3 },
  { merk: 'Porsche',       model: 'Taycan Cross Turismo',  accuKwh: 93,  kwhPer100Km: 22.0, maxAcKw: 22,  fase: 3 },
  { merk: 'Porsche',       model: 'Macan Electric',        accuKwh: 100, kwhPer100Km: 21.0, maxAcKw: 22,  fase: 3 },
  { merk: 'Porsche',       model: 'Cayenne E-Hybrid (PHEV)',   accuKwh: 22, kwhPer100Km: 27.0, maxAcKw: 11, fase: 3 },
  { merk: 'Porsche',       model: 'Panamera 4 E-Hybrid (PHEV)',accuKwh: 21, kwhPer100Km: 26.0, maxAcKw: 11, fase: 3 },

  // ── Renault ──────────────────────────────────────────────────────────────────
  { merk: 'Renault',       model: '5 E-Tech',              accuKwh: 52,  kwhPer100Km: 14.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Renault',       model: 'Zoe',                   accuKwh: 52,  kwhPer100Km: 17.0, maxAcKw: 22,  fase: 3 },
  { merk: 'Renault',       model: 'Megane E-Tech',         accuKwh: 60,  kwhPer100Km: 16.0, maxAcKw: 22,  fase: 3 },
  { merk: 'Renault',       model: 'Scenic E-Tech',         accuKwh: 87,  kwhPer100Km: 17.5, maxAcKw: 22,  fase: 3 },
  { merk: 'Renault',       model: '4 E-Tech Electric',     accuKwh: 52,  kwhPer100Km: 13.0, maxAcKw: 11,  fase: 3 },

  // ── Skoda ────────────────────────────────────────────────────────────────────
  { merk: 'Skoda',         model: 'Enyaq',                 accuKwh: 77,  kwhPer100Km: 17.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Skoda',         model: 'Enyaq Coupé',           accuKwh: 77,  kwhPer100Km: 17.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Skoda',         model: 'Elroq',                 accuKwh: 82,  kwhPer100Km: 18.0, maxAcKw: 11,  fase: 3 },

  // ── Smart ────────────────────────────────────────────────────────────────────
  { merk: 'Smart',         model: '#1',                    accuKwh: 66,  kwhPer100Km: 17.0, maxAcKw: 22,  fase: 3 },
  { merk: 'Smart',         model: '#3',                    accuKwh: 66,  kwhPer100Km: 16.5, maxAcKw: 22,  fase: 3 },

  // ── Subaru ───────────────────────────────────────────────────────────────────
  { merk: 'Subaru',        model: 'Solterra',              accuKwh: 71,  kwhPer100Km: 21.0, maxAcKw: 11,  fase: 3 },

  // ── Suzuki ───────────────────────────────────────────────────────────────────
  { merk: 'Suzuki',        model: 'e Vitara',              accuKwh: 49,  kwhPer100Km: 16.5, maxAcKw: 7.4, fase: 1 },

  // ── Tesla ────────────────────────────────────────────────────────────────────
  { merk: 'Tesla',         model: 'Model 3',               accuKwh: 60,  kwhPer100Km: 14.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Tesla',         model: 'Model Y',               accuKwh: 75,  kwhPer100Km: 16.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Tesla',         model: 'Model S',               accuKwh: 100, kwhPer100Km: 18.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Tesla',         model: 'Model X',               accuKwh: 100, kwhPer100Km: 22.0, maxAcKw: 11,  fase: 3 },

  // ── Toyota ───────────────────────────────────────────────────────────────────
  { merk: 'Toyota',        model: 'bZ4X',                  accuKwh: 71,  kwhPer100Km: 19.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Toyota',        model: 'C-HR PHEV',             accuKwh: 14,  kwhPer100Km: 19.0, maxAcKw: 3.7, fase: 1 },
  { merk: 'Toyota',        model: 'RAV4 PHEV',             accuKwh: 18,  kwhPer100Km: 20.0, maxAcKw: 3.7, fase: 1 },
  { merk: 'Toyota',        model: 'Prius PHEV',            accuKwh: 14,  kwhPer100Km: 18.5, maxAcKw: 3.7, fase: 1 },

  // ── Volkswagen ───────────────────────────────────────────────────────────────
  { merk: 'Volkswagen',    model: 'ID.3',                  accuKwh: 58,  kwhPer100Km: 15.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Volkswagen',    model: 'ID.4',                  accuKwh: 77,  kwhPer100Km: 17.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Volkswagen',    model: 'ID.5',                  accuKwh: 77,  kwhPer100Km: 17.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Volkswagen',    model: 'ID.7',                  accuKwh: 77,  kwhPer100Km: 16.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Volkswagen',    model: 'ID.Buzz',               accuKwh: 77,  kwhPer100Km: 21.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Volkswagen',    model: 'Golf GTE (PHEV)',       accuKwh: 19,  kwhPer100Km: 14.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Volkswagen',    model: 'Tiguan eHybrid (PHEV)', accuKwh: 19,  kwhPer100Km: 14.7, maxAcKw: 11,  fase: 3 },
  { merk: 'Volkswagen',    model: 'Passat eHybrid (PHEV)', accuKwh: 19,  kwhPer100Km: 20.0, maxAcKw: 11,  fase: 3 },

  // ── Volvo ────────────────────────────────────────────────────────────────────
  { merk: 'Volvo',         model: 'EX30',                  accuKwh: 69,  kwhPer100Km: 15.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Volvo',         model: 'EX40',                  accuKwh: 78,  kwhPer100Km: 19.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Volvo',         model: 'EC40',                  accuKwh: 82,  kwhPer100Km: 19.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Volvo',         model: 'EX90',                  accuKwh: 111, kwhPer100Km: 21.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Volvo',         model: 'XC60 Recharge (PHEV)',  accuKwh: 15,  kwhPer100Km: 17.0, maxAcKw: 3.7, fase: 1 },
  { merk: 'Volvo',         model: 'XC90 Recharge (PHEV)',  accuKwh: 15,  kwhPer100Km: 25.0, maxAcKw: 3.7, fase: 1 },

  // ── Xpeng ────────────────────────────────────────────────────────────────────
  { merk: 'Xpeng',         model: 'G6',                    accuKwh: 87,  kwhPer100Km: 17.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Xpeng',         model: 'G9',                    accuKwh: 98,  kwhPer100Km: 19.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Xpeng',         model: 'P7',                    accuKwh: 80,  kwhPer100Km: 16.5, maxAcKw: 11,  fase: 3 },

  // ── Zeekr ────────────────────────────────────────────────────────────────────
  { merk: 'Zeekr',         model: '001',                   accuKwh: 100, kwhPer100Km: 18.0, maxAcKw: 11,  fase: 3 },
  { merk: 'Zeekr',         model: 'X',                     accuKwh: 66,  kwhPer100Km: 16.5, maxAcKw: 11,  fase: 3 },
  { merk: 'Zeekr',         model: '7X',                    accuKwh: 100, kwhPer100Km: 19.0, maxAcKw: 11,  fase: 3 },
];

/** Fallback for "Ander model / Weet ik niet" */
export const AUTO_FALLBACK: AutoModel = {
  merk: 'Anders',
  model: 'Ander model / Weet ik niet',
  accuKwh: 65,
  kwhPer100Km: 18.0,
  maxAcKw: 11,
  fase: 3,
};

/**
 * Label for the fallback brand option — always shown as last item in the
 * brand picker. When selected, AUTO_FALLBACK values are used for calculation.
 */
export const MERK_FALLBACK_LABEL = 'Mijn merk staat er (nog) niet bij';

/**
 * Complete brand list (44 brands), alphabetically sorted.
 * Brands with specific models in AUTO_MODELLEN show a model dropdown.
 * Brands without specific models automatically use AUTO_FALLBACK values.
 */
export const MERKEN: string[] = [
  'Abarth',
  'Alfa Romeo',
  'Alpine',
  'Audi',
  'BMW',
  'BYD',
  'Citroën',
  'Cupra',
  'Dacia',
  'DS Automobiles',
  'Fiat',
  'Ford',
  'Genesis',
  'Honda',
  'Hyundai',
  'Jaguar',
  'Jeep',
  'Kia',
  'Leapmotor',
  'Lexus',
  'Lotus',
  'Lynk & Co',
  'Maserati',
  'Mazda',
  'Mercedes',
  'MG',
  'Mini',
  'Nissan',
  'Opel',
  'Peugeot',
  'Polestar',
  'Porsche',
  'Renault',
  'Skoda',
  'Smart',
  'Subaru',
  'Suzuki',
  'Tesla',
  'Toyota',
  'Volkswagen',
  'Volvo',
  'Xpeng',
  'Zeekr',
];

/** Orientation categories (for users without an EV yet) */
export const ORIENTATIE_CATEGORIEEN = [
  'Compacte gezinsauto (bijv. Peugeot e-208, VW ID.3)',
  'Middenklasse SUV (bijv. VW ID.4, Hyundai IONIQ 5)',
  'Premium sedan (bijv. Tesla Model 3, BMW i4)',
  'Grote SUV / MPV (bijv. VW ID.Buzz, Volvo EX40)',
  'Plug-in hybride (PHEV)',
  'Ik weet het nog niet',
] as const;

export type OrientatieCategorieId = (typeof ORIENTATIE_CATEGORIEEN)[number];

/** Sentinel for the PHEV orientation category */
export const ORIENTATIE_CAT_PHEV: OrientatieCategorieId = 'Plug-in hybride (PHEV)';

/** EV orientation types — maps each EV category to a representative model for the calculation */
export interface EvOrientatieType {
  categorie: OrientatieCategorieId;
  model: AutoModel;
}

export const EV_ORIENTATIE_TYPEN: EvOrientatieType[] = [
  {
    categorie: 'Compacte gezinsauto (bijv. Peugeot e-208, VW ID.3)',
    model: { merk: 'Volkswagen', model: 'ID.3', accuKwh: 58, kwhPer100Km: 15.5, maxAcKw: 11, fase: 3 },
  },
  {
    categorie: 'Middenklasse SUV (bijv. VW ID.4, Hyundai IONIQ 5)',
    model: { merk: 'Volkswagen', model: 'ID.4', accuKwh: 77, kwhPer100Km: 17.5, maxAcKw: 11, fase: 3 },
  },
  {
    categorie: 'Premium sedan (bijv. Tesla Model 3, BMW i4)',
    model: { merk: 'Tesla', model: 'Model 3', accuKwh: 60, kwhPer100Km: 14.5, maxAcKw: 11, fase: 3 },
  },
  {
    categorie: 'Grote SUV / MPV (bijv. VW ID.Buzz, Volvo EX40)',
    model: { merk: 'Volkswagen', model: 'ID.Buzz', accuKwh: 77, kwhPer100Km: 21.0, maxAcKw: 11, fase: 3 },
  },
  {
    categorie: 'Ik weet het nog niet',
    model: { ...AUTO_FALLBACK },
  },
];

/** PHEV sub-types for orientation — shown when user picks Plug-in hybride */
export interface PhevOrientatieType {
  id: string;
  label: string;
  /** Representative model used for calculation */
  model: AutoModel;
}

export const PHEV_ORIENTATIE_TYPEN: PhevOrientatieType[] = [
  {
    id: 'compacte_phev',
    label: 'Compacte PHEV (bijv. VW Golf GTE, Audi A3 PHEV)',
    model: { merk: 'Volkswagen', model: 'Golf GTE (PHEV)', accuKwh: 19, kwhPer100Km: 14.5, maxAcKw: 11, fase: 3 },
  },
  {
    id: 'middenklasse_phev',
    label: 'Middenklasse PHEV (bijv. BMW 530e, Mercedes C 300e)',
    model: { merk: 'BMW', model: '530e (PHEV)', accuKwh: 19, kwhPer100Km: 21.0, maxAcKw: 11, fase: 3 },
  },
  {
    id: 'suv_phev',
    label: 'SUV plug-in hybride (bijv. BMW X5, Kia Sportage PHEV)',
    model: { merk: 'BMW', model: 'X5 xDrive50e (PHEV)', accuKwh: 25, kwhPer100Km: 25.0, maxAcKw: 11, fase: 3 },
  },
  {
    id: 'weet_niet_phev',
    label: 'Weet ik nog niet',
    model: { merk: 'Anders', model: 'Plug-in hybride (onbekend)', accuKwh: 15, kwhPer100Km: 18.0, maxAcKw: 7.4, fase: 1 },
  },
];
